import AppResource from './AppResource';
import Engine from '../Engine';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import ApiFilter from '../Api/ApiFilter';
import AppResourceCollection from './AppResourceCollection';
import ApiRelation from "../Api/ApiRelation";

class AppResourceSearch {

    constructor(resourceType, config = {}) {

        const {apiPath, environment} = {apiPath: 'search', environment: null, ...config};
        this._apiPath = apiPath;
        this._environment = environment;
        this._forceSimplePagination = false;
        this._loaded = false;
        this._loading = false;
        this._pagination = true;
        this._resourceType = resourceType;
        this._meta = null;
        this._fieldset = null;
        this._include = [];
        this._filter = null;
        this._scopes = null;
        this._withCount = null;
        this._limit = null;
        this.orderBy = {
            createdAt: 'desc',
        };
        this._appendAttributes = [];

        this._page = 1;
        this._pageSize = 10;
        this._appResourceCollection = new AppResourceCollection(resourceType);
        const appResourceClass = Engine.appResourceClass(resourceType);
        this._coreAppResource = new appResourceClass({
            resourceType: resourceType,
        });

        this.vPaginator = {
            page: 1,
            perPage: 1,
            total: 0,
        };
        this._keepPaginationWithFilters = false;
    }

    /**
     *
     * @param searchData
     * @param shouldRequest
     * @param sync
     */
    async setSearchData(searchData, shouldRequest = true, sync = false) {
        const loaded = this.loaded;
        this._loaded = false;
        if (typeof searchData !== 'object') {
            return;
        }

        if (searchData.hasOwnProperty('orderBy')) {
            this.orderBy = searchData.orderBy;
        } else {
            this.orderBy = {};
        }

        if (
            searchData.hasOwnProperty('pagination') &&
            (
                searchData.pagination === 'disabled' ||
                isEmpty(searchData.pagination)
            )
        ) {
            this._pagination = false;
        } else {
            if (searchData.hasOwnProperty('page')) {
                this.page = parseInt(searchData.page);
            } else {
                this.page = 1;
            }

            if (searchData.hasOwnProperty('pageSize')) {
                this.pageSize = parseInt(searchData.pageSize);
            } else {
                this.pageSize = 10;
            }
        }

        if (searchData.hasOwnProperty('include')) {
            this.include = searchData.include;
        } else {
            this.include = [];
        }

        if (searchData.hasOwnProperty('fieldset')) {
            this.fieldset = searchData.fieldset;
        } else {
            this._fieldset = null;
        }

        if (searchData.hasOwnProperty('filter')) {
            this.filter = searchData.filter;
        } else {
            this._filter = null;
        }

        if (searchData.hasOwnProperty('scopes')) {
            this.scopes = searchData.scopes;
        } else {
            this._scopes = null;
        }

        if (searchData.hasOwnProperty('withCount')) {
            this.withCount = searchData.withCount;
        } else {
            this._withCount = null;
        }

        if (searchData.hasOwnProperty('limit')) {
            this.limit = searchData.limit;
        }


        if (searchData.hasOwnProperty('appendAttributes')) {
            this.appendAttributes = searchData.appendAttributes;
        } else {
            this.appendAttributes = [];
        }

        this._loaded = loaded;
        if (this.loaded && shouldRequest) {
            if (sync) {
                await this.search();
            } else {
                this.search().then();
            }
        }

        return this;
    }

    async setSearchDataSync(searchData, shouldRequest = true) {
        await this.setSearchData(searchData, shouldRequest, true);
    }

    get appResourceCollection() {
        return this._appResourceCollection;
    }

    /**
     * @returns {null|{}}
     */
    get orderBy() {
        return this._orderBy;
    }

    /**
     *
     * @param value
     */
    set orderBy(value) {
        const orderBy = {};
        this._orderBy = orderBy;
        if (typeof value !== 'object') {
            return;
        }

        this.checkOrderBy(value);
        this._orderBy = value;

        if (this.loaded) {
            this.load().then();
        }
    }

    /**
     * @param value
     */
    checkOrderBy(value) {
        Object.keys(value).forEach((key) => {
            if (value[key] !== null && typeof value[key] === 'object') {
                this.checkOrderBy(value[key]);
                return;
            }

            if (['asc', 'asc nulls first', 'desc', 'desc nulls last'].includes(
                value[key]) < 0) {
                value[key] = 'asc';
            }
        });
    }

    /**
     * @returns {[]|Array}
     */
    get include() {
        return this._include;
    }

    /**
     * @param value
     */
    set include(value) {
        this._include = {};

        if (Array.isArray(value)) {
            this._include = value;
            return;
        }

        for (let relation in value) {
            if (
                !value.hasOwnProperty(relation) ||
                !(value[relation] instanceof ApiRelation)
            ) {
                continue;
            }
            this._include[relation] = value[relation].toObject();
        }
    }

    /**
     * @returns {[]|Array}
     */
    get appendAttributes() {
        return this._appendAttributes;
    }

    /**
     * @param value
     */
    set appendAttributes(value) {
        this._appendAttributes = [];
        if (!Array.isArray(value)) {
            return;
        }
        this._appendAttributes = value;
    }

    /**
     * @returns {[]|Array}
     */
    get fieldset() {
        return this._fieldset;
    }

    /**
     * @param value
     */
    set fieldset(value) {
        this._fieldset = value;
    }

    /**
     * @returns {null|ApiFilter}
     */
    get filter() {
        return this._filter;
    }

    /**
     * @returns {null|Array}
     */
    get scopes() {
        return this._scopes;
    }

    /**
     * @returns {null|ApiFilter}
     */
    get withCount() {
        return this._withCount;
    }

    /**
     * @returns {null|ApiFilter}
     */
    get limit() {
        return this._limit;
    }

    /**
     * @returns {AppResource}
     */
    get coreAppResource() {
        return this._coreAppResource;
    }

    /**
     *
     */
    clearFilter() {
        this._filter = null;
        this.clearData();
        if (this.loaded) {
            this.load().then();
        }
    }

    /**
     * @param value
     */
    set filter(value) {
        if (!(value instanceof ApiFilter)) {
            console.warn(
                'The filter must be an instance of ApiFilter. Skipped.');
            return;
        }
        let forceReload = false;
        if (
            this.filter === null ||
            !isEqual(this.filter.toObject(), value.toObject())
        ) {
            this.clearData();
            forceReload = this._loaded;
        }
        this._filter = value;
        if (forceReload) {
            this.load().then();
        }
    }

    /**
     * @param scopes
     */
    set scopes(scopes) {
        if (typeof scopes !== 'object') {
            console.warn(
                'The scopes parameter must be an object. Skipped.');
            return;
        }
        this._scopes = scopes;
    }

    set withCount(value) {
        if (value && !(value instanceof ApiFilter)) {
            console.warn(
                'The withCount parameter must be an instance of ApiFilter. Skipped.');
            return;
        }

        this._withCount = value;
    }

    set limit(value) {
        if (!Number.isInteger(value) || value <= 0) {
            console.warn(
                'The limit parameter must be positive integer. Skipped.');
            return;
        }

        if (this.pagination) {
            console.warn(
                'The limit parameter must applied only if pagination is false. Skipped.');
            return;
        }
        this._limit = value;
    }

    /**
     * @returns {boolean}
     */
    get pagination() {
        return this._pagination;
    }

    /**
     * @param value
     */
    set pagination(value) {
        this._pagination = value;
    }

    /**
     *
     * @returns {number}
     */
    get page() {
        return this._page;
    }

    /**
     * @param pageNo
     */
    set page(pageNo) {
        let intPage = parseInt(pageNo);
        if (isNaN(intPage)) {
            intPage = 1;
        }
        this._page = intPage;
        if (
            this.loaded &&
            this.currentPage !== intPage
        ) {
            this._forceSimplePagination = true;
            const inst = this;
            this.load().then(function () {
                inst._forceSimplePagination = false;
            });
        }
    }

    set pageNumber(pageNo) {
        let intPage = parseInt(pageNo);

        if (isNaN(intPage)) {
            intPage = 1;
        }

        this._page = intPage;
    }

    /**
     * @returns {number}
     */
    get pageSize() {
        return this._pageSize;
    }

    /**
     * @param pageSize
     */
    set pageSize(pageSize) {
        let intPageSize = parseInt(pageSize);
        if (isNaN(intPageSize)) {
            intPageSize = 10;
        }
        let reload = false;
        if (this.loaded && this.pageSize !== intPageSize) {
            reload = true;
        }

        this._pageSize = intPageSize;
        if (reload) {
            this.clearData();
            this.load().then();
        }
    }

    /**
     * @returns {boolean}
     */
    get loaded() {
        return this._loaded;
    }

    /**
     * @returns {boolean}
     */
    get loading() {
        return this._loading;
    }

    /**
     *
     * @returns {{}|*}
     */
    get appResources() {
        return this._appResourceCollection.appResources;
    }

    /**
     *
     * @param value
     */
    set appResources(value) {
        this._appResourceCollection.appResources = value;
    }

    /**
     *
     * @returns {null|{perPage: number, currentPage: number, lastPage: number, from: number, last: number, total: number}}
     */
    get meta() {
        return this._meta;
    }

    /**
     * @returns {null|number}
     */
    get currentPage() {
        if (this.meta === null || !this.meta.hasOwnProperty('currentPage')) {
            return null;
        }
        return parseInt(this.meta.currentPage);
    }

    /**
     * @returns {null|number}
     */
    get lastPage() {
        if (this.meta === null || !this.meta.hasOwnProperty('lastPage')) {
            return null;
        }
        return parseInt(this.meta.lastPage);
    }

    /**
     * @returns {null|number}
     */
    get total() {
        if (this.meta === null || !this.meta.hasOwnProperty('total')) {
            return null;
        }
        return parseInt(this.meta.total);
    }

    /**
     * @returns {Promise<AppResourceSearch>}
     */
    async search(appendResults = false) {
        return this.load(appendResults);
    }

    /**
     *
     * @returns {Promise<AppResourceSearch>}
     */
    async load(appendResults = false) {
        const url = this._coreAppResource.apiPaths(this._environment)[this._apiPath];
        const requestData = {};
        if (this._forceSimplePagination) {
            requestData.pagination = 'simple';
        }
        if (this._pagination || this._forceSimplePagination) {
            requestData.page = this.page;
            requestData.pageSize = this.pageSize;
        } else {
            requestData.pagination = 'disabled';
        }
        if (!isEmpty(this.include)) {
            requestData.include = this.include;
        }
        if (!isEmpty(this.fieldset)) {
            requestData.fieldset = this.fieldset;
        }
        if (!isEmpty(this.orderBy)) {
            requestData.orderBy = this.orderBy;
        }
        if (this.filter !== null && this.filter instanceof ApiFilter) {
            requestData.filter = this.filter.toObject();
        }
        if (this.scopes !== null && typeof this.scopes === 'object') {
            requestData.scopes = this.scopes;
        }

        if (this.withCount !== null && this.withCount instanceof ApiFilter) {
            requestData.withCount = this.withCount.toObject();
        }

        if (Number.isInteger(this.limit) && this.limit > 0 && this.pagination === false) {
            requestData.limit = this.limit;
        }

        if (!isEmpty(this.appendAttributes)) {
            requestData.appendAttributes = this.appendAttributes;
        }

        this._loading = true;

        const apiResponse = await Engine.post(url, requestData);

        this._loading = false;

        const resources = apiResponse.resources ||
            new AppResourceCollection(this._resourceType);

        if (appendResults) {
            if (!this._appResourceCollection) {
                this._appResourceCollection = new AppResourceCollection(
                    this._resourceType);
            }

            resources.each((resource) => {
                this._appResourceCollection.addResource(resource);
            });

        } else {
            this._appResourceCollection = resources;
        }

        if (!this._forceSimplePagination) {
            this._meta = !isEmpty(apiResponse.meta) ? apiResponse.meta : null;
        } else {
            this._meta.currentPage = this._page;
        }
        this.vPaginator = {
            page: (this._meta) ? this._meta.currentPage : this._page,
            perPage: (this._meta) ? this._meta.perPage : this._pageSize,
            total: (this._meta) ? this._meta.total : this.length,
        };

        this._loaded = true;
    }

    /**
     *
     */
    clearData() {
        this._meta = {};
        this.vPaginator = {
            page: 1,
            perPage: 1,
            total: 0,
        };

        if (!this._keepPaginationWithFilters) {
            this._page = 1;
        }
    }

    /**
     * @param appResource
     */
    addResource(appResource) {
        this._appResourceCollection.addResource(appResource);
    }

    /**
     * @param resourceUuid
     * @returns {null|AppResource}
     */
    getResource(resourceUuid) {
        return this._appResourceCollection.getResource(resourceUuid);
    }

    /**
     * @returns {number}
     */
    get length() {
        return this._appResourceCollection.length;
    }

    [Symbol.iterator]() {
        const values = this._appResourceCollection.toArray();
        const length = this._appResourceCollection.length;
        let current = 0;
        const iterator = {
            next() {
                if (current < length) {
                    const value = values[current];
                    current++;

                    return {
                        done: false,
                        value: value,
                    };
                }

                return {done: true, value: undefined};
            },
        };

        return iterator;
    }

    async loadMore() {
        this._page = this.page + 1;
        return await this.load(true);
    }

    async loadPage(pageNo) {

        let intPage = parseInt(pageNo);
        if (isNaN(intPage)) {
            intPage = 1;
        }
        this._page = intPage;
        if (
            this.loaded &&
            this.currentPage !== intPage
        ) {
            this._forceSimplePagination = true;
            const inst = this;
            await this.load();
            inst._forceSimplePagination = false;
        }
    }


    /**
     * @returns {string}
     */
    get apiPath() {
        return this._apiPath;
    }

    /**
     * @param value
     */
    set apiPath(value) {
        if (typeof value !== 'string') {
            return;
        }
        this._apiPath = value;
    }

    /**
     * @returns {string}
     */
    get environment() {
        return this._environment;
    }

    /**
     * @param value
     */
    set environment(value) {
        if (typeof value !== 'string') {
            return;
        }
        this._environment = value;
    }

    /**
     * @param value
     */
    set keepPaginationWithFilters(value) {
        this._keepPaginationWithFilters = value;
    }

    /**
     * @returns {boolean}
     */
    get keepPaginationWithFilters() {
        return this._keepPaginationWithFilters;
    }
}

export default AppResourceSearch;
